import {
  ICounterData,
  IOrganizationUser,
  ORGANIZATION_STATUS,
} from "@home-health-notify/domain";
import { Box, ListItemButton, ListItemIcon, Tooltip } from "@mui/material";
import {
  DeveloperSettingsContext,
  getSocketStatusConfig,
  OrganizationContext,
  useTranslation,
} from "hhn-commons";
import { useContext, useEffect, useState } from "react";
import { Link } from "react-router";
import OrganizationStyledBadge from "./OrganizationStyledBadge";
import { SocketStatusDot } from "./SocketStatusDot";
import { storeOrganizationId } from "../utils/organizationIdStorage";

const SidebarListItem = ({ orgUser }: { orgUser: IOrganizationUser }) => {
  const { t } = useTranslation();
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { developerModeEnabled } = useContext(DeveloperSettingsContext);
  const org = orgUser.getOrganization();
  const currentSelectedOrg = selectedOrgUser?.isSameOrganization(org) || false;
  const url = `/organizations/${org.organizationId}/chats`;
  // Use getCounters() to get the current counter value, not the stale org.unreadAlertCount
  const [unreadCount, setUnreadCount] = useState(
    orgUser.getCounters().alertCounter || 0,
  );

  useEffect(() => {
    const removeCountersListener = orgUser?.countersListeners?.(
      (counters: ICounterData) => {
        setUnreadCount(counters.alertCounter);
      },
    );
    return () => {
      removeCountersListener?.();
    };
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  // Socket status for this org
  const socketStatus = orgUser.getSocketManager().connectionStatus;
  const socketConfig = getSocketStatusConfig(
    socketStatus.isConnected,
    socketStatus.isConnecting,
    socketStatus.retryCount,
  );

  const pendingSuffix =
    org.organizationStatus === ORGANIZATION_STATUS.PENDING
      ? ` - ${t("expiringSoon", { ns: "organization" })}`
      : "";

  // Enhanced tooltip with status when developer mode is on
  const tooltipText = developerModeEnabled
    ? `${org.organizationName}${pendingSuffix} - ${socketConfig.text}`
    : `${org.organizationName}${pendingSuffix}`;

  const handleOrganizationIdStorage = () => {
    if (!currentSelectedOrg) {
      storeOrganizationId(org.organizationId);
    }
  };
  return (
    <Tooltip
      title={tooltipText}
      placement="right"
      key={"sidebar" + org.organizationId}
    >
      <Box component="span">
        <ListItemButton
          disabled={!orgUser.isOrganizationActive()}
          {...(!currentSelectedOrg && orgUser.isOrganizationActive()
            ? { component: Link, to: url }
            : {})}
          onClick={handleOrganizationIdStorage}
          data-testid="org-list-item"
          sx={{
            paddingLeft: "12px",
            paddingRight: 0,
            ...(currentSelectedOrg ? { cursor: "default" } : {}),
          }}
        >
          <ListItemIcon sx={{ position: "relative" }}>
            <OrganizationStyledBadge
              org={org}
              isActive={currentSelectedOrg}
              unreadCount={unreadCount}
            />
            {developerModeEnabled && (
              <SocketStatusDot
                isConnected={socketStatus.isConnected}
                isConnecting={socketStatus.isConnecting}
                orgUser={orgUser}
              />
            )}
          </ListItemIcon>
        </ListItemButton>
      </Box>
    </Tooltip>
  );
};

export default SidebarListItem;
